// frontend/src/components/cohesion/EntityDrawer.Space.tsx
// Space (venue) panel for the universal entity drawer.

import { useEffect, useState } from 'react'
import { useLanguage } from '../../i18n/index.jsx'
import api from '../../utils/api.js'

type Lang = { t: (k: string) => string }

interface Props {
  id: number
  navigate: (to: string) => void
  closeDrawer: () => void
}

interface Space {
  id: number
  name: string
  city?: string
  capacity?: number
}

export default function EntityDrawerSpace({ id, navigate, closeDrawer }: Props) {
  const { t } = useLanguage() as Lang
  const [data, setData] = useState<Space | null>(null)
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    api.get<Space>(`/venues/${id}`)
      .then((r) => { if (!cancelled) setData(r.data) })
      .catch((e) => { if (!cancelled) setError(String(e?.response?.data?.error ?? e?.message ?? 'load failed')) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  if (loading) return <div className="entity-drawer-loading">{t('loading')}</div>
  if (error)   return <div className="entity-drawer-error" role="alert">{error}</div>
  if (!data)   return <div className="entity-drawer-empty">{t('itemNotFound')}</div>

  const openSpace = () => {
    closeDrawer()
    navigate(`/spaces/${data.id}`)
  }

  return (
    <>
      <button type="button" className="entity-drawer-title-link" onClick={openSpace}>
        {data.name}
      </button>
      <div className="entity-drawer-meta">
        {data.city && (
          <div className="entity-drawer-meta-row">
            <span className="entity-drawer-meta-key">{t('city')}</span>
            <span className="entity-drawer-meta-val">{data.city}</span>
          </div>
        )}
        {typeof data.capacity === 'number' && (
          <div className="entity-drawer-meta-row">
            <span className="entity-drawer-meta-key">{t('capacity')}</span>
            <span className="entity-drawer-meta-val">{data.capacity}</span>
          </div>
        )}
      </div>
      <div className="entity-drawer-actions">
        <button type="button" className="entity-drawer-action" onClick={openSpace}>
          {t('openWorkspace')}
        </button>
      </div>
    </>
  )
}
